import { changed, noOp, rejected, type Command, type CommandExecution } from './commands';
import { damage } from './damage';
import { createId, findLayer, walkLayers, type EditorDocument } from './model';

export const GLOBAL_SWATCHES_EXTENSION = 'maipo:global-swatches';
export const TEXT_SWATCH_EXTENSION = 'maipo:text-swatch';

export interface GlobalSwatch { id: string; name: string; color: string }

export function globalSwatches(document: EditorDocument): GlobalSwatch[] {
  const value = document.extensions[GLOBAL_SWATCHES_EXTENSION];
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is GlobalSwatch => Boolean(entry && typeof entry === 'object'
    && typeof (entry as GlobalSwatch).id === 'string' && typeof (entry as GlobalSwatch).name === 'string'
    && /^#[\da-f]{6}$/i.test((entry as GlobalSwatch).color))).map((entry) => structuredClone(entry));
}

export function createGlobalSwatch(color: string, name?: string): GlobalSwatch {
  return { id: createId('swatch'), name: name?.trim() || color.toUpperCase(), color: color.toLowerCase() };
}

export class SetGlobalSwatchesCommand implements Command {
  readonly label: string;
  private before?: unknown;
  private hadBefore = false;
  constructor(private readonly swatches: readonly GlobalSwatch[], label = 'Update global swatches') { this.label = label; }
  retainedBytes(): number { return 128 + JSON.stringify(this.swatches).length * 2 + JSON.stringify(this.before ?? null).length * 2; }
  execute(document: EditorDocument): CommandExecution {
    if (JSON.stringify(globalSwatches(document)) === JSON.stringify(this.swatches)) return noOp();
    if (this.before === undefined) {
      this.hadBefore = Object.hasOwn(document.extensions, GLOBAL_SWATCHES_EXTENSION);
      this.before = structuredClone(document.extensions[GLOBAL_SWATCHES_EXTENSION] ?? null);
    }
    if (this.swatches.length === 0) delete document.extensions[GLOBAL_SWATCHES_EXTENSION];
    else document.extensions[GLOBAL_SWATCHES_EXTENSION] = structuredClone(this.swatches);
    return changed();
  }
  undo(document: EditorDocument): void {
    if (this.hadBefore) document.extensions[GLOBAL_SWATCHES_EXTENSION] = structuredClone(this.before);
    else delete document.extensions[GLOBAL_SWATCHES_EXTENSION];
  }
}

type LinkedPaintSnapshot =
  | { kind: 'fill' | 'stroke'; layerId: string; objectId: string; color: string }
  | { kind: 'text'; layerId: string; color: string };

export function linkedPaints(document: EditorDocument, swatchId: string): LinkedPaintSnapshot[] {
  const snapshots: LinkedPaintSnapshot[] = [];
  for (const layer of walkLayers(document.root)) {
    if (layer.kind === 'vector') for (const object of layer.vector.objects) {
      if (object.fill?.swatchId === swatchId) snapshots.push({ kind: 'fill', layerId: layer.id, objectId: object.id, color: object.fill.color });
      if (object.stroke?.paint.swatchId === swatchId) snapshots.push({ kind: 'stroke', layerId: layer.id, objectId: object.id, color: object.stroke.paint.color });
    }
    if (layer.kind === 'text' && layer.text.extensions[TEXT_SWATCH_EXTENSION] === swatchId) snapshots.push({ kind: 'text', layerId: layer.id, color: layer.text.style.color });
  }
  return snapshots;
}

function setSnapshotColor(document: EditorDocument, snapshot: LinkedPaintSnapshot, color: string): void {
  const layer = findLayer(document.root, snapshot.layerId);
  if (snapshot.kind === 'text') {
    if (layer?.kind === 'text') layer.text.style.color = color;
    return;
  }
  if (layer?.kind !== 'vector') return;
  const object = layer.vector.objects.find((entry) => entry.id === snapshot.objectId);
  if (snapshot.kind === 'fill' && object?.fill) object.fill.color = color;
  if (snapshot.kind === 'stroke' && object?.stroke) object.stroke.paint.color = color;
}

export class UpdateGlobalSwatchCommand implements Command {
  readonly label = 'Edit global swatch';
  private beforeSwatches: GlobalSwatch[] = [];
  private beforePaints: LinkedPaintSnapshot[] = [];
  constructor(private readonly swatchId: string, private readonly color: string) {}
  retainedBytes(): number { return 128 + JSON.stringify(this.beforeSwatches).length * 2 + JSON.stringify(this.beforePaints).length * 2; }
  execute(document: EditorDocument): CommandExecution {
    const swatches = globalSwatches(document);
    const swatch = swatches.find((entry) => entry.id === this.swatchId);
    if (!swatch) return rejected('Global swatch no longer exists.');
    if (swatch.color === this.color) return noOp();
    // Snapshot before mutating: `swatch` is a member of `swatches`, so keeping
    // the array by reference and then writing through `swatch` would make the
    // undo record hold the new colour. The artwork came back on undo (its
    // colours are snapshotted by value below) while the swatch stayed changed,
    // leaving the document disagreeing with itself.
    this.beforeSwatches = structuredClone(swatches);
    this.beforePaints = linkedPaints(document, this.swatchId);
    swatch.color = this.color;
    document.extensions[GLOBAL_SWATCHES_EXTENSION] = swatches;
    for (const snapshot of this.beforePaints) setSnapshotColor(document, snapshot, this.color);
    return changed(damage({ fullDocument: true }));
  }
  undo(document: EditorDocument): void {
    document.extensions[GLOBAL_SWATCHES_EXTENSION] = structuredClone(this.beforeSwatches);
    for (const snapshot of this.beforePaints) setSnapshotColor(document, snapshot, snapshot.color);
  }
}

export class ApplyGlobalSwatchCommand implements Command {
  readonly label = 'Apply global swatch';
  private before?: unknown;
  constructor(private readonly layerId: string, private readonly objectIds: readonly string[], private readonly swatch: GlobalSwatch) {}
  retainedBytes(): number { return 256 + JSON.stringify(this.before ?? null).length * 2; }
  execute(document: EditorDocument): CommandExecution {
    const layer = findLayer(document.root, this.layerId);
    if (layer?.kind === 'text') {
      this.before ??= { color: layer.text.style.color, link: layer.text.extensions[TEXT_SWATCH_EXTENSION] };
      layer.text.style.color = this.swatch.color;
      layer.text.extensions[TEXT_SWATCH_EXTENSION] = this.swatch.id;
      return changed(damage({ layerIds: [layer.id] }));
    }
    if (layer?.kind !== 'vector') return rejected('Select vector artwork or text to apply a global swatch.');
    const targets = layer.vector.objects.filter((object) => this.objectIds.includes(object.id));
    if (targets.length === 0) return rejected('Select at least one vector object.');
    this.before ??= targets.map((object) => ({ id: object.id, fill: structuredClone(object.fill) }));
    for (const object of targets) object.fill = { ...(object.fill ?? { type: 'solid', alpha: 1 }), color: this.swatch.color, swatchId: this.swatch.id };
    return changed(damage({ layerIds: [layer.id] }));
  }
  undo(document: EditorDocument): void {
    const layer = findLayer(document.root, this.layerId);
    if (layer?.kind === 'text' && this.before && !Array.isArray(this.before)) {
      const value = this.before as { color: string; link?: unknown };
      layer.text.style.color = value.color;
      if (value.link === undefined) delete layer.text.extensions[TEXT_SWATCH_EXTENSION];
      else layer.text.extensions[TEXT_SWATCH_EXTENSION] = value.link;
    }
    if (layer?.kind === 'vector' && Array.isArray(this.before)) for (const snapshot of this.before as Array<{ id: string; fill: typeof layer.vector.objects[number]['fill'] }>) {
      const object = layer.vector.objects.find((entry) => entry.id === snapshot.id);
      if (object) object.fill = structuredClone(snapshot.fill);
    }
  }
}
